import { Component, inject, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ICON } from '@core/util/icon';
import { Note } from '@models/matter-notes';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { MatterNotesDispatch, MatterNotesState } from '@state/matter/notes';
import { MODAL_DATA } from '@ui/modal/modal-data.token';
import { ModalService } from '@ui/modal/modal.service';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CreateNoteData {
	id?: string;
}

@UntilDestroy()
@Component({
	selector: 'app-create-note',
	templateUrl: './create-note.component.html',
	standalone: false,
})
export class CreateNoteComponent implements OnInit {
	modalService = inject(ModalService);
	public id: string;
	public submitted = false;
	public ICON = ICON;
	public note: Note = {
		title: '',
		description: '',
		pinned: false,
	};

	public first50CharDescription = '';

	@Select(MatterNotesState.get) getNoteFn$: Observable<(id: string) => Note>;
	@ViewSelectSnapshot(AuthState.getLoggedInUser) authUser: AuthUser;

	constructor(
		@Inject(MODAL_DATA) public data: CreateNoteData,
		private noteDispatch: MatterNotesDispatch,
	) {}

	ngOnInit(): void {
		this.id = this.data?.id ?? null;

		if (this.id) {
			this.getNote(this.id).subscribe((note) => {
				if (note) {
					this.note = cloneDeep(note);
				}
			});
		}
	}

	togglePinned(): void {
		this.note.pinned = !this.note.pinned;
	}

	getNote(id: string): Observable<Note> {
		return this.getNoteFn$.pipe(
			untilDestroyed(this),
			map((fn) => fn(id)),
		);
	}

	get ownerViewing() {
		return this.authUser.uid === this.note.creatorId;
	}

	setFirst50CharDescription(description: string): void {
		this.first50CharDescription = description.substring(0, 50);
	}

	createUpdate(form: NgForm) {
		this.submitted = true;
		const title = this.note.title || this.first50CharDescription;
		if (form.valid) {
			if (this.id) {
				this.noteDispatch.Update(
					this.id,
					title,
					this.note.description,
					this.note.pinned,
				);
			} else {
				this.noteDispatch.Create(
					title,
					this.note.description,
					this.note.pinned,
				);
			}
			this.modalService.closeModal();
		}
	}

	close() {
		this.modalService.closeModal();
	}
}
